import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { CopyToClipboard } from '../../../components/CopyToClipboard';
import { TargetTypeLabel } from '../../../components/Curation/Labels';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { IUserSegmentGroup, UserSegmentType } from '../../../services/types';
import { RouteNames } from '../../config';
import useStyles from './styles';

interface IProps {
  data?: IUserSegmentGroup[];
  onClickRow: (segmentId: number) => void;
  onUpdate: (data: IUserSegmentGroup[]) => void;
  onError: (error: Error) => void;
  onDelete: (seg: IUserSegmentGroup, type: UserSegmentType, idx: number) => void;
  onClickCuration: (segmentItem: IUserSegmentGroup, mode: 'create' | 'edit') => void;
  onToggleStatus: (seg: IUserSegmentGroup, type: UserSegmentType) => void;
}

export const GroupList = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const history = useHistory();

  const [childrenAnchorEl, setChildrenAnchorEl] = useState<null | HTMLElement>(null);
  const [childAnchorId, setChildAnchorId] = useState<null | number>(null);

  if (props?.data?.length === 0) {
    return (
      <Paper>
        <Box display="flex" justifyContent="center" alignItems="center" className={classes.noDataBox} flexDirection="column">
          <Typography>No data</Typography>
          <Typography variant="caption">{'Tap the "Create User Segment" at the top to create a first user segment.'}</Typography>
        </Box>
      </Paper>
    );
  }

  const handlePopoverClose = () => {
    setChildrenAnchorEl(null);
    setChildAnchorId(null);
  };

  return (
    <div className={classes.root}>
      <Paper>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell align="center" className={classes.segNumberColumn}>
                Number of
                <br />
                Segment
              </TableCell>
              <TableCell align="center" className={classes.segNumberColumn}>
                Segment
                <br />
                ID
              </TableCell>
              <TableCell>Segment Title</TableCell>
              <TableCell align="center" className={classes.segNumberColumn}>
                User Count
              </TableCell>
              <TableCell align="center" className={classes.segCurationBtnColumn}>
                Curation
              </TableCell>
              <TableCell align="center" className={classes.segCurationInfoColumn}></TableCell>
              <TableCell align="center" className={classes.segNumberColumn}>
                Delete
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data &&
              props.data.length > 0 &&
              props.data.map((seg, idx) => {
                return (
                  <TableRow
                    component={Link}
                    to={`${RouteNames.UserSegment}/v/${seg.id}`}
                    key={`segment-group-list-item-${seg.id}`}
                    hover
                    className={classes.tableRow}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      if (!seg.id) {
                        return;
                      }
                      if (childrenAnchorEl === null) {
                        props.onClickRow(seg.id);
                        history.push(`${RouteNames.UserSegment}/v/${seg.id}`);
                      } else {
                        e.stopPropagation();
                      }
                    }}
                  >
                    <TableCell align="center">
                      <span
                        className={classes.overLine}
                        onClick={event => {
                          event.stopPropagation();
                          event.preventDefault();
                          setChildrenAnchorEl(event.currentTarget);
                          setChildAnchorId(seg.id!);
                        }}
                      >
                        {seg.children?.length || 0}
                      </span>
                      {childAnchorId === seg.id && (
                        <Popover
                          id={`children-popover-${seg.id}`}
                          open={true}
                          anchorEl={childrenAnchorEl}
                          onClose={handlePopoverClose}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                        >
                          <Box padding={2} width={400}>
                            <Grid container spacing={1}>
                              <Grid item xs={12}>
                                This group segment contains with
                              </Grid>
                              {seg.children?.map(childSeg => (
                                <Grid item xs={12} key={`child-seg-key-${childSeg.id!}`}>
                                  <Grid container alignContent="stretch">
                                    <Chip size="small" label={'Activated'} color={'primary'} style={{ marginRight: 8 }} />
                                    <div className={classes.childSegName}>
                                      {childSeg.name}({childSeg.userCount?.toLocaleString() || 0})
                                    </div>
                                    <div className={classes.childSegId}>
                                      <CopyToClipboard content={childSeg.id!} />
                                    </div>
                                  </Grid>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Popover>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <CopyToClipboard content={seg.id!} />
                    </TableCell>
                    <TableCell>{seg.name}</TableCell>
                    <TableCell align="center">{seg.userCount?.toLocaleString() || 0}</TableCell>
                    <TableCell align="center">
                      {(seg.featuredCardNum || 0) + (seg.collectionNum || 0) === 0 ? (
                        <Button
                          // href={`${RouteNames.UserSegment}/v/${seg.id}`}
                          variant="outlined"
                          color="primary"
                          className={classes.segButton}
                          onClick={e => {
                            props.onClickCuration(seg, 'create');
                            history.push(`${RouteNames.UserSegment}/v/${seg.id}/curation/create`);
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          Create
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          color="default"
                          className={classes.segButton}
                          onClick={e => {
                            props.onClickCuration(seg, 'edit');
                            history.push(`${RouteNames.UserSegment}/v/${seg.id}/curation`);
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="left">
                      <Box className={classes.abtestCountBox}>
                        <Box className={classes.abtestCountBoxLeft}>Featured Card: {seg.featuredCardNum}</Box>
                        <Box className={classes.abtestCountBoxRight}>{seg.isFeaturedCardsOnTest && <TargetTypeLabel type={'A/B'} />}</Box>
                      </Box>
                      <Box className={classes.abtestCountBox}>
                        <Box className={classes.abtestCountBoxLeft}>Collection: {seg.collectionNum}</Box>
                        <Box className={classes.abtestCountBoxRight}>{seg.isCollectionsOnTest && <TargetTypeLabel type={'A/B'} />}</Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={''} placement="left-end">
                        <IconButton
                          color={'primary'}
                          disableFocusRipple={seg.isActive}
                          disableTouchRipple={seg.isActive}
                          disableRipple={seg.isActive}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            props.onDelete(seg, UserSegmentType.Group, idx);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
};
